import { useState, useEffect } from "react";
import { http, API, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Users, ChevronDown, Check, Loader2, CreditCard, AlertTriangle, ExternalLink, Share2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function Workshops() {
  useSEO("workshops");
  const [settings, setSettings] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [faq, setFaq] = useState([]);
  const [pastWorkshops, setPastWorkshops] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      http.get("/workshops/public/settings"),
      http.get("/workshops/public/workshops"),
      http.get("/workshops/public/faq"),
      http.get("/workshops/public/past-workshops"),
      http.get("/workshops/public/testimonials"),
    ]).then(([s, w, f, pw, t]) => { setSettings(s.data); setWorkshops(w.data); setFaq(f.data); setPastWorkshops(pw.data); setTestimonials(t.data); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !settings) return <Layout><div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="text-[#D4AF37] animate-spin" size={32} /></div></Layout>;

  const upcoming = workshops.filter(w => !["completed", "archived", "cancelled"].includes(w.status));

  return (
    <Layout>
      <div className="min-h-screen bg-[#0A0A0A]">
        {/* SEO JSON-LD for upcoming workshops */}
        {upcoming.length > 0 && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": upcoming.map((w, i) => ({
              "@type": "ListItem", "position": i + 1,
              "item": {
                "@type": "Event", "name": w.title, "description": w.description,
                "startDate": w.date, "endDate": w.date,
                "location": { "@type": "Place", "name": w.location_name, "address": w.address },
                "offers": { "@type": "Offer", "price": (w.price / 100).toFixed(2), "priceCurrency": "USD", "availability": w.seats_remaining > 0 ? "https://schema.org/InStock" : "https://schema.org/SoldOut" },
                "organizer": { "@type": "Organization", "name": "Mirror Conversations\u2122" },
                "url": `${window.location.origin}/workshops/${w.slug}`,
              }
            }))
          })}} />
        )}

        {/* Hero */}
        <section className="relative py-28 md:py-36 px-6 text-center" data-testid="ws-hero">
          {settings.hero_image && <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${resolveImage(settings.hero_image)})` }} />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0A0A0A]" />
          <div className="relative z-10 max-w-3xl mx-auto">
            <motion.h1 initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#F5F5F0] leading-tight mb-6">{settings.hero_headline}</motion.h1>
            <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="font-body text-base text-zinc-400 leading-relaxed mb-10 max-w-xl mx-auto">{settings.hero_text}</motion.p>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#upcoming" className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-4 rounded-full font-ui tracking-wide uppercase text-sm hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all">{settings.hero_btn_primary}</a>
              {upcoming[0] && <Link to={`/workshops/${upcoming[0].slug}`} className="border border-[#D4AF37] text-[#D4AF37] px-8 py-4 rounded-full font-ui tracking-wide uppercase text-sm hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">{settings.hero_btn_secondary}</Link>}
            </motion.div>
          </div>
        </section>

        {/* Upcoming */}
        <section id="upcoming" className="py-20 px-6 max-w-5xl mx-auto" data-testid="ws-upcoming">
          <h2 className="font-display text-3xl md:text-4xl text-[#F5F5F0] text-center mb-12">Upcoming Workshops</h2>
          {upcoming.length === 0 ? (
            <p className="text-zinc-500 text-center py-16 font-body">No upcoming workshops at this time. Check back soon.</p>
          ) : (
            <div className="space-y-6">
              {upcoming.map(w => <WorkshopCard key={w.id} w={w} settings={settings} />)}
            </div>
          )}
        </section>

        {/* Testimonials */}
        {testimonials.length > 0 && (
          <section className="py-20 px-6 max-w-5xl mx-auto" data-testid="ws-testimonials">
            <h2 className="font-display text-3xl text-[#F5F5F0] text-center mb-3">What Attendees Say</h2>
            <p className="font-body text-sm text-zinc-500 text-center mb-12">Real reflections from real participants.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.slice(0, 6).map(t => (
                <motion.div key={t.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
                  <div className="flex gap-1">{Array.from({ length: t.rating || 5 }).map((_, i) => (
                    <span key={i} className="text-[#D4AF37] text-xs">&#9733;</span>
                  ))}</div>
                  <p className="font-body text-sm text-zinc-300 italic leading-relaxed line-clamp-4">&ldquo;{t.text}&rdquo;</p>
                  <div>
                    <p className="font-body text-xs text-[#F5F5F0]">{t.name}</p>
                    {t.workshop_title && <p className="font-body text-[10px] text-zinc-600">{t.workshop_title}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Past Workshops */}
        {pastWorkshops.length > 0 && (
          <section className="py-20 px-6 max-w-5xl mx-auto" data-testid="ws-past">
            <h2 className="font-display text-3xl text-[#F5F5F0] text-center mb-3">Past Workshops</h2>
            <p className="font-body text-sm text-zinc-500 text-center mb-12">A look back at the conversations that have already changed lives.</p>
            <div className="space-y-6">
              {pastWorkshops.map(w => (
                <motion.div key={w.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
                  className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid={`ws-past-${w.id}`}>
                  <div className="md:flex">
                    {w.featured_image && (
                      <div className="md:w-72 shrink-0"><img src={resolveImage(w.featured_image)} alt={w.title} className="w-full h-48 md:h-full object-cover opacity-70" /></div>
                    )}
                    <div className="p-6 md:p-8 flex-1">
                      <span className="font-ui text-[9px] tracking-[0.3em] text-zinc-500 uppercase">Completed · {w.date}</span>
                      <h3 className="font-display text-xl text-[#F5F5F0] mt-1 mb-2">{w.title}</h3>
                      {w.recap && <p className="font-body text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{w.recap}</p>}
                      {w.key_takeaways?.length > 0 && (
                        <div className="mt-4">
                          <p className="font-ui text-[10px] tracking-wider text-[#D4AF37] uppercase mb-2">Key Takeaways</p>
                          <ul className="space-y-1">{w.key_takeaways.map((t, i) => (
                            <li key={i} className="flex items-start gap-2"><Check size={12} className="text-[#D4AF37] mt-0.5 shrink-0" /><span className="font-body text-xs text-zinc-400">{t}</span></li>
                          ))}</ul>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="py-20 px-6 max-w-3xl mx-auto" data-testid="ws-faq">
            <h2 className="font-display text-3xl text-[#F5F5F0] text-center mb-12">Frequently Asked Questions</h2>
            <FaqAccordion faq={faq} />
          </section>
        )}

        {/* Disclaimer */}
        <section className="py-12 px-6 max-w-3xl mx-auto">
          <div className="rounded-xl border border-amber-500/15 bg-amber-500/[0.02] p-5 flex gap-3">
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="font-body text-[11px] text-zinc-400 leading-relaxed whitespace-pre-line">{settings.disclaimer}</p>
          </div>
        </section>
      </div>
    </Layout>
  );
}

/* ═══ WORKSHOP CARD ═══ */
function WorkshopCard({ w, settings }) {
  const seatText = (() => {
    if (w.status === "sold_out") return "Sold Out";
    if (settings.seat_display === "limited" && w.seats_remaining <= 3) return "Limited Availability";
    if (settings.seat_display === "soldout_only") return "";
    if (w.seats_remaining <= 0) return "Sold Out";
    return `${w.seats_remaining} Seat${w.seats_remaining !== 1 ? "s" : ""} Remaining`;
  })();
  const isOpen = ["published", "registration_open", "almost_full"].includes(w.status);
  const isWaitlist = w.status === "waitlist_open" || w.status === "sold_out";

  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
      className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid={`ws-card-${w.id}`}>
      <div className="md:flex">
        {w.featured_image && (
          <div className="md:w-80 shrink-0">
            <img src={resolveImage(w.featured_image)} alt={w.title} className="w-full h-48 md:h-full object-cover" />
          </div>
        )}
        <div className="p-6 md:p-8 flex-1">
          {w.featured && <span className="font-ui text-[9px] tracking-[0.3em] text-[#D4AF37] uppercase">Featured</span>}
          <h3 className="font-display text-2xl text-[#F5F5F0] mt-1 mb-2">{w.title}</h3>
          {w.subtitle && <p className="font-body text-sm text-zinc-400 mb-4">{w.subtitle}</p>}

          <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
            <div className="flex items-center gap-2 text-zinc-400"><Calendar size={14} className="text-[#D4AF37]" /> <span className="font-body">{w.day_of_week}, {w.date}</span></div>
            <div className="flex items-center gap-2 text-zinc-400"><Clock size={14} className="text-[#D4AF37]" /> <span className="font-body">{w.start_time} – {w.end_time} ({w.duration})</span></div>
            <div className="flex items-center gap-2 text-zinc-400"><MapPin size={14} className="text-[#D4AF37]" /> <span className="font-body truncate">{w.location_name}</span></div>
            <div className="flex items-center gap-2 text-zinc-400"><Users size={14} className="text-[#D4AF37]" /> <span className="font-body">{seatText || `${w.capacity} seats`}</span></div>
          </div>

          <p className="font-body text-sm text-zinc-400 mb-6 line-clamp-3">{w.description}</p>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="font-display text-2xl text-[#D4AF37]">{w.display_price}<span className="text-zinc-500 text-sm font-body"> / person</span></span>
            <div className="flex gap-2">
              <Link to={`/workshops/${w.slug}`} className="font-ui text-xs tracking-wide text-zinc-400 border border-white/10 px-4 py-2.5 rounded-full hover:border-zinc-600 transition-all">Learn More</Link>
              {isOpen && <Link to={`/workshops/${w.slug}#register`} className="font-ui text-xs tracking-wide bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-full hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] transition-all" data-testid={`ws-claim-${w.id}`}>{w.btn_text}</Link>}
              {isWaitlist && <Link to={`/workshops/${w.slug}#waitlist`} className="font-ui text-xs tracking-wide border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 rounded-full hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Join the Waitlist</Link>}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function FaqAccordion({ faq }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2">
      {faq.map((f, i) => (
        <div key={f.id} className="rounded-xl border border-white/[0.06] overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
            <span className="font-body text-sm text-[#F5F5F0] pr-4">{f.question}</span>
            <ChevronDown size={16} className={`text-[#D4AF37] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <div className="px-5 pb-5"><p className="font-body text-sm text-zinc-400 leading-relaxed">{f.answer}</p></div>}
        </div>
      ))}
    </div>
  );
}
